<template>
  <div class="ls-navbar-container" :class="themedClass">
    <!--Desktop -->
    <nav v-if="screenSize.desktop" class="ls-navbar" role="navigation" aria-label="main navigation">
      <RouterLink
        :to="{ name: isPropertyManager ? 'properties' : 'services' }"
        test-id="logo"
        @click.native="closeBothDropdowns"
      >
        <img class="wordmark" :src="isLawnStarter ? lsLogo : ''" :alt="brandName" />
      </RouterLink>

      <!-- Desktop main and user links -->
      <div class="right-nav-links" test-id="right-nav-links">
        <transition name="fade">
          <div v-if="!hideLinks" test-id="main-links" class="main-links">
            <NavBarMainLinks
              :is-property-manager="isPropertyManager"
              @closeMenu="closeBothDropdowns"
            />
            <div class="vert-line"></div>
          </div>
        </transition>
        <div class="user-menu" test-id="user-menu" @click="toggleUserDropdown">
          <LsIcon type="user" color="primary-light" size="md" />
          <span class="user-name" test-id="user-name">{{ auth.user | fullName }}</span>
          <span class="chevron" test-id="chevron">
            <LsIcon type="chevron" color="primary-light" size="md" />
          </span>
        </div>

        <!-- Desktop user dropdown  -->
        <transition name="fade">
          <div
            v-if="showUserDropdown"
            v-on-clickaway="closeBothDropdowns"
            class="user-dropdown"
            test-id="user-dropdown"
          >
            <div v-if="!hideLinks" class="upper-links">
              <RouterLink
                class="user-link"
                :to="{ name: 'profile' }"
                test-id="profile"
                @click.native="closeBothDropdowns"
                >Your Profile</RouterLink
              >
              <RouterLink
                v-if="!isCustomerLead"
                class="user-link"
                :to="{ name: 'billing' }"
                test-id="billing"
                @click.native="closeBothDropdowns"
                >Billing Details</RouterLink
              >
            </div>
            <span
              :class="['user-link sign-out', { 'property-manager': hideLinks }]"
              test-id="sign-out"
              @click="signOut"
              >Sign Out</span
            >
          </div>
        </transition>
      </div>
    </nav>

    <!-- Mobile -->
    <nav v-else class="ls-navbar" role="navigation" aria-label="main navigation">
      <!-- Mobile left side user icon  -->
      <span class="mobile-user-icon" test-id="user" @click="toggleUserDropdown">
        <LsIcon type="user" color="primary-light" size="xlg" />
      </span>

      <RouterLink
        :to="{ name: isPropertyManager ? 'properties' : 'services' }"
        @click.native="closeBothDropdowns"
      >
        <img class="wordmark" :src="isLawnStarter ? lsLogo : ''" :alt="brandName" />
      </RouterLink>

      <!-- Mobile right side hamburger/cancel icons -->
      <div class="mobile-menu-control">
        <transition name="fade">
          <div
            v-if="!hideLinks"
            class="hamburger-wrapper"
            test-id="mobile_menu"
            @click="toggleMainDropdown"
          >
            <span :class="['mobile-menu-icon one', { active: showMainDropdown }]" />
            <span :class="['mobile-menu-icon two', { active: showMainDropdown }]" />
            <span :class="['mobile-menu-icon three', { active: showMainDropdown }]" />
          </div>
        </transition>

        <!-- Mobile main links dropdown -->
        <transition name="fade">
          <div
            v-if="showMainDropdown"
            class="mobile-main-menu"
            test-id="mobile-main-menu"
            :style="{ height: mobileDropdownHeight }"
          >
            <transition name="fade">
              <NavBarMainLinks
                :is-property-manager="isPropertyManager"
                @closeMenu="toggleMainDropdown"
              />
            </transition>
            <div
              :style="{ top: mobileDropdownHeight }"
              class="overlay"
              @click="toggleMainDropdown"
            ></div>
          </div>
        </transition>

        <!--Mobile user links dropdown -->
        <transition name="fade">
          <div
            v-if="showUserDropdown"
            class="user-dropdown"
            :style="{ height: mobileDropdownHeight }"
          >
            <div v-if="!hideLinks" class="upper-links">
              <span class="user-link">
                <RouterLink :to="{ name: 'profile' }" @click.native="closeBothDropdowns"
                  >Your Profile</RouterLink
                >
              </span>
              <span v-if="!isCustomerLead" class="user-link">
                <RouterLink :to="{ name: 'billing' }" @click.native="closeBothDropdowns"
                  >Billing Details</RouterLink
                >
              </span>
            </div>
            <span
              :class="['user-link sign-out', { 'property-manager': hideLinks }]"
              test-id="sign-out"
              @click="signOut"
              >Sign Out</span
            >
            <div
              v-if="showUserDropdown"
              class="overlay"
              test-id="overlay"
              :style="{ top: mobileDropdownHeight }"
              @click="closeBothDropdowns"
            ></div>
          </div>
        </transition>
      </div>
    </nav>

    <div v-if="!hideLinks" class="ls-navbar-spacer"></div>
    <LsNotificationBanner />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';

import { LsIcon } from '@/components/common';
import LsNotificationBanner from '@/components/LsNotificationBanner/LsNotificationBanner.vue';
import NavBarMainLinks from '@/components/LsNavbar/NavBarMainLinks.vue';
import { themedComponentMixin } from '@/mixins';

import { AUTH__SIGN_OUT, AUTH__IS_CUSTOMER_LEAD, AUTH__IS_PROPERTY_MANAGER } from '@/store/types';

import LsLogo from '@/assets/lawnstarter.svg';

export default {
  name: 'LsNavbar',
  components: {
    LsIcon,
    NavBarMainLinks,
    LsNotificationBanner,
  },
  mixins: [clickaway, themedComponentMixin],
  data() {
    return {
      lsLogo: LsLogo,
      showUserDropdown: false,
      showMainDropdown: false,
    };
  },
  computed: {
    ...mapGetters({
      isCustomerLead: [AUTH__IS_CUSTOMER_LEAD],
      isPropertyManager: [AUTH__IS_PROPERTY_MANAGER],
    }),
    ...mapState(['auth', 'screenSize']),

    // needed for tests
    currentRouteName() {
      return this.$route.name;
    },
    mobileDropdownHeight() {
      if (this.isCustomerLead || this.hideLinks) {
        return this.showUserDropdown ? '120px' : '60px';
      }

      return '200px';
    },
    hideLinks() {
      return this.currentRouteName === 'properties';
    },
  },
  watch: {
    screenSize: {
      handler() {
        this.showMainDropdown = false;
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions([AUTH__SIGN_OUT]),
    toggleMainDropdown() {
      this.showMainDropdown = !this.showMainDropdown;
      this.showUserDropdown = false;
    },
    toggleUserDropdown() {
      this.showUserDropdown = !this.showUserDropdown;
      this.showMainDropdown = false;
    },
    closeBothDropdowns() {
      this.showUserDropdown = false;
      this.showMainDropdown = false;
    },
    signOut() {
      this.showUserDropdown = false;
      this[AUTH__SIGN_OUT]();
    },
  },
};
</script>

<style lang="scss" scoped>
$mobile-dropdown-height: 200px;

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.ls-navbar-container {
  margin-bottom: 40px;
  position: relative;
}

.ls-navbar {
  height: $navbar-height;
  font-size: $size-4;
  position: fixed;
  top: 0;
  left: 0;
  z-index: $z-index-navbar;
  min-width: 100%;
  background-color: $primary;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .right-nav-links {
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 10px;
  }

  .main-links {
    display: flex;
    align-items: center;
  }

  .vert-line {
    height: 40px;
    width: 1px;
    background-color: $special-primary-hover;
  }

  .user-menu {
    display: flex;
    align-items: center;
    padding-left: 30px;
    color: $white;

    .user-name {
      padding: 0 16px;
      font-size: $size-4;
      user-select: none;
    }

    .chevron {
      transform: rotate(90deg);
    }
  }

  .user-dropdown {
    position: absolute;
    right: 15px;
    top: 62px;
    width: 226px;
    background-color: $white;
    border: 1px solid $primary;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 20px;

    &:after {
      border-bottom: 10px solid $white;
      border-left: 10px solid transparent;
      border-right: 10px solid transparent;
      content: '';
      height: 0;
      position: absolute;
      right: 12px;
      top: -10px;
    }

    .line {
      height: 1px;
      width: 100%;
      background-color: $primary-light;
      margin: 10px 0;
    }

    a,
    .user-link {
      width: 100%;
      color: $primary;
      font-size: $size-4;
      user-select: none;
      font-weight: normal;
      &:hover {
        color: $special-primary-hover;
      }
    }
  }

  .upper-links {
    height: 70%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-around;
    padding-top: 7px;
    padding-bottom: 17px;
    border-bottom: 1px solid $special-primary-hover;

    .user-link:not(:first-of-type) {
      margin-top: 14px;
    }
  }

  .sign-out {
    margin-top: 13px;

    &.property-manager {
      margin-top: 0;
    }
  }

  .user-link,
  .hamburger-wrapper,
  .user-menu,
  .mobile-user-icon {
    cursor: pointer;
  }

  .hamburger-wrapper {
    position: absolute;
    top: 24px;
    right: 22px;
    width: 3.6rem;
    height: 2.8rem;
  }

  .mobile-menu-icon {
    position: absolute;
    left: 2px;
    width: 3.2rem;
    height: 0.3rem;
    background-color: $white;
    transition: all 0.2s ease-in-out;
  }

  .one {
    top: 2px;

    &.active {
      transform: rotate(45deg);
      right: 3px;
      top: 12px;
    }
  }

  .two {
    top: 12px;
    &.active {
      transform: scale(0);
    }
  }

  .three {
    top: 22px;
    &.active {
      transform: rotate(-45deg);
      top: 12px;
    }
  }

  .mobile-main-menu {
    position: absolute;
    top: $navbar-height;
    left: 0;
    width: 100%;
    background-color: $primary;
  }

  .overlay {
    position: absolute;
    left: 0;
    width: 100%;
    height: 100vh;
    background-color: $special-navbar-overlay;
    opacity: 0.7;
  }
}

.ls-navbar-spacer {
  height: $navbar-height;
}

@media (max-width: $mobile-max-width) {
  .ls-navbar-container {
    margin-bottom: 20px;
  }

  // nesting required
  .ls-navbar {
    .user-dropdown {
      left: 0;
      top: $navbar-height;
      width: 100%;
      border: none;
      align-items: center;
      justify-content: space-around;
      &:after {
        border-top: 10px solid $primary;
        top: 0px;
        right: 100%;
        left: 24px;
      }

      a,
      .user-link {
        width: 100%;
        display: flex;
        justify-content: center;
        font-size: $size-2;
        font-weight: normal;
      }
    }

    .wordmark {
      padding-right: 26px;
      user-select: none;
    }

    .upper-links {
      width: 100%;
      padding-top: 0px;
      padding-bottom: 10px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      .user-link:not(:first-of-type) {
        margin-top: 22px;
      }
    }

    .sign-out {
      margin-top: 10px;

      .property-manager {
        margin-top: 0;
      }
    }
  }
}

.themed-component {
  .ls-navbar,
  .mobile-main-menu {
    background-color: $theme-color-primary;

    .user-dropdown {
      border-color: $theme-color-primary;

      &:after {
        border-top-color: $theme-color-primary;
      }

      .line {
        background-color: $theme-color-primary-light;
      }

      .upper-links {
        border-bottom-color: $theme-color-primary;
      }

      a,
      .user-link {
        color: $theme-color-primary;
        &:hover {
          color: $theme-color-primary;
          opacity: 0.65;
        }
      }
    }
  }

  .wordmark {
    content: $theme-logo-white;
  }

  .vert-line {
    border-color: $theme-logo-white;
    opacity: 0.5;
  }
}
</style>
